import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { HierarchyService } from './shared/services/hierarchy.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { FormControl, FormGroup } from '@angular/forms';
import { HierarchyRootObject } from './shared/models/hierarchy-root-object.model';
import { HierarchyObject } from './shared/models/hierarchy-object.model';
import { Edge, Node } from '@swimlane/ngx-graph';
import { getRandomBrightColor } from '../shared/utils/color';
import { HierarchyType } from './shared/models/hierarchy-type.model';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatButtonToggle } from '@angular/material/button-toggle';

@Component({
  selector: 'app-hierarchies',
  templateUrl: './hierarchies.component.html',
  styleUrls: ['./hierarchies.component.scss'],
})
export class HierarchiesComponent implements OnInit {
  protected mobile: boolean = false;
  protected isLoading: boolean = false;
  protected showColumnFilter: boolean = true;

  private hierarchyTypes: HierarchyType[] = [];
  protected selectableHierarchyTypes: HierarchyType[] = [];

  protected selectedHierarchyType: HierarchyType | undefined;
  protected readonly hierarchyRootObjects: MatTableDataSource<HierarchyRootObject> =
    new MatTableDataSource<HierarchyRootObject>();
  protected selectedElement: HierarchyRootObject | undefined;

  protected readonly columns: string[] = [
    'hierarchyObjectId',
    'hierarchyObjectTypeName',
    'extDeviceId',
  ];
  protected attributeColumns: string[] = [];

  protected get combinedColumns(): string[] {
    return [...this.columns, ...this.attributeColumns];
  }

  protected get filteredColumns(): string[] {
    return this.combinedColumns.filter((col) =>
      this.rootObjectsColumnsToggle.includes(col)
    );
  }

  protected rootObjectsColumnsToggle: string[] = [
    'hierarchyObjectId',
    'hierarchyObjectTypeName',
    'extDeviceId',
  ];

  @ViewChild('rootObjectsPaginator') private paginator!: MatPaginator;
  @ViewChild(MatSort) private sort: MatSort = new MatSort();
  @ViewChild('graphDialog') private graphDialog!: TemplateRef<any>;
  @ViewChild('rootObjectsGroup') rootObjectsGroup: MatButtonToggle | undefined;
  @ViewChild('nodeTemplate') nodeTemplate!: TemplateRef<any>;

  protected readonly filters = new FormGroup({
    hierarchyObjectId: new FormControl(''),
    hierarchyObjectTypeName: new FormControl(''),
    extDeviceId: new FormControl(''),
  });

  protected clearFilter(name: string, $event: MouseEvent) {
    $event.stopPropagation();
    this.filters.get(name)?.setValue('');
  }

  protected emptyFilter(name: string): boolean {
    return this.filters.get(name)?.value === '';
  }

  protected filterHierarchyTypes(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
    this.selectableHierarchyTypes =
      this.hierarchyTypes.filter((ht) =>
        ht.hierarchyTypeName.toLowerCase().includes(filterValue)
      ) || [];
  }

  protected readonly graphSettings = {
    layout: {
      orientation: 'TB',
    },
    autoZoom: false,
    zoomLevel: 1,
    minZoomLevel: 0.1,
    maxZoomLevel: 3,
    autoCenter: true,
    draggingEnabled: true,
    showMiniMap: true,
    nodes: Array<Node>(),
    links: Array<Edge>(),
  };

  constructor(
    private hierarchyService: HierarchyService,
    private breakpointObserver: BreakpointObserver,
    public dialog: MatDialog
  ) {
    this.hierarchyRootObjects.filterPredicate = this.tableFilter();
  }

  ngOnInit(): void {
    this.hierarchyService.getHierarchyTypes().subscribe({
      next: (data) => {
        this.hierarchyTypes = data.hierarchyTypes || [];
        this.selectableHierarchyTypes = this.hierarchyTypes || [];
      },
    });
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (res) => {
        this.mobile = res.matches;
        this.showColumnFilter = !this.mobile;
        if (!this.mobile) {
          this.rootObjectsColumnsToggle = [
            'hierarchyObjectId',
            'hierarchyObjectTypeName',
            'extDeviceId',
            ...this.attributeColumns,
          ];
        } else {
          this.rootObjectsColumnsToggle = [
            'hierarchyObjectTypeName',
            'extDeviceId',
          ];
          if (this.paginator) {
            this.paginator.pageSize = 5;
          }
        }
      },
    });
    this.filters.valueChanges.subscribe((filter) => {
      this.hierarchyRootObjects.filter = JSON.stringify(filter);
    });
  }

  protected selectHierarchyType() {
    if (this.selectedHierarchyType === undefined) return;
    this.isLoading = true;
    this.hierarchyRootObjects.data = [];
    this.selectedElement = undefined;
    this.hierarchyService
      .getHierarchyRootObjectsByTypeId(
        this.selectedHierarchyType.hierarchyTypeId
      )
      .subscribe({
        next: (data) => {
          this.attributeColumns =
            data.hierarchyRootObjects[0]?.attributes?.map(
              (a) => a.attributeName
            ) || [];
          this.hierarchyRootObjects.data = data.hierarchyRootObjects || [];

          this.hierarchyRootObjects.paginator = this.paginator;
          this.hierarchyRootObjects.sort = this.sort;

          if (!this.mobile) {
            this.rootObjectsColumnsToggle = [
              'hierarchyObjectId',
              'hierarchyObjectTypeName',
              'extDeviceId',
              ...this.attributeColumns,
            ];
          } else {
            this.rootObjectsColumnsToggle = [
              'hierarchyObjectTypeName',
              'extDeviceId',
            ];
          }
        },
      })
      .add(() => {
        this.isLoading = false;
      });
  }

  private tableFilter(): (data: any, filter: string) => boolean {
    return (data: HierarchyRootObject, filter: string): boolean => {
      const searchTerms = JSON.parse(filter);
      return Object.keys(searchTerms)
        .filter((s) => searchTerms[s])
        .every((key) => {
          const value = data[key as keyof HierarchyRootObject]
            .toString()
            .toLowerCase();
          return value.includes(searchTerms[key].toString().toLowerCase());
        });
    };
  }

  protected onRowClick(row: HierarchyRootObject) {
    if (this.selectedHierarchyType === undefined) return;
    this.isLoading = true;
    this.selectedElement = row;
    this.graphSettings.nodes = [];
    this.graphSettings.links = [];
    this.hierarchyService
      .getHierarchyObjectsByRootId(
        this.selectedHierarchyType.hierarchyTypeId,
        this.selectedElement.hierarchyObjectId
      )
      .subscribe({
        next: (data) => {
          this.generateNodesAndLinks(data.hierarchyObjects || []);
          this.openHierarchiesDialog();
        },
      })
      .add(() => {
        this.isLoading = false;
      });
  }

  private generateNodesAndLinks(data: HierarchyObject[]) {
    const nodes: Node[] = [];
    const links: Edge[] = [];
    const colorMap = new Map<number, string>();
    data.forEach((object) => {
      let randomHsl = colorMap.get(object.hierarchyObjectTypeId);
      if (randomHsl === undefined) {
        randomHsl = getRandomBrightColor();
        colorMap.set(object.hierarchyObjectTypeId, randomHsl);
      }
      const idString = 'ID' + object.hierarchyObjectId.toString();
      nodes.push({
        id: idString,
        label: object.hierarchyObjectTypeName,
        data: { customColor: randomHsl },
      });

      if (object.parentHierarchyObjectId) {
        links.push({
          id: 'L' + this.graphSettings.links.length.toString(),
          source: 'ID' + object.parentHierarchyObjectId.toString(),
          target: idString,
          label: 'link',
        });
      }
    });
    this.graphSettings.nodes = [...nodes];
    this.graphSettings.links = [...links];
  }

  private openHierarchiesDialog() {
    const dialogRef = this.dialog.open(this.graphDialog, {
      panelClass: ['mat-dialog-override', 'dialog-responsive'],
    });
    dialogRef.afterClosed().subscribe({
      next: () => {
        this.selectedElement = undefined;
      },
    });
  }

  private zoomInterval: NodeJS.Timeout | undefined;

  private incrementZoom(zoomIn: boolean = true) {
    this.graphSettings.zoomLevel = zoomIn
      ? Math.min(
          this.graphSettings.maxZoomLevel,
          this.graphSettings.zoomLevel + 0.1
        )
      : Math.max(
          this.graphSettings.minZoomLevel,
          this.graphSettings.zoomLevel - 0.1
        );
  }

  protected zoom(zoomIn: boolean = true) {
    this.incrementZoom(zoomIn);
    this.zoomInterval = setInterval(() => this.incrementZoom(zoomIn), 100);
  }

  protected stopZoom() {
    if (this.zoomInterval) {
      clearInterval(this.zoomInterval);
    }
  }

  protected readonly close = close;
}
